import { v4 as uuid } from 'uuid';

type ModelConstructor = new (json: { [prop: string]: any }, key?: string) => DatabaseModel;
export type ModelJson = { [key: string]: any };
export type ModelMap<T extends DatabaseModel> = { [key: string]: T };

export abstract class DatabaseModel {
    abstract key: string;

    public abstract fromJson(json: any);
    public abstract toJson(): ModelJson;

    constructor(json?: ModelJson, key?: string) { };

    /**
     * @deprecated Use static `DatabaseModel.genKey()`
     * @returns A unique key
     */
    public genKey(): string {
        return DatabaseModel.genKey();
    }

    /**
     * Generate a unique key
     * @returns A unique key
     */
    public static genKey(): string {
        const originalUUID = uuid();
        const uuidWithoutHyphens = originalUUID.replace(/-/g, '');
        // Convert to base64 (Base64 encoding utilizes a 64-character set, allowing it to represent binary data more compactly)
        // Convert the hex string to a byte array
        const byteArray = new Uint8Array(uuidWithoutHyphens.match(/.{1,2}/g).map(byte => parseInt(byte, 16)));
        // Convert the byte array to a binary string using map
        const binaryString = Array.from(byteArray).map(byte => String.fromCharCode(byte)).join('');
        // Base64 encode the binary string
        const base64UUID = btoa(binaryString).replace(/=/g, ''); // Remove '=' padding
        const urlSafeBase64 = base64UUID.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
        // Trim to 20 characters (we can not user more than 20 characters in GyG setup)
        const shorterUUID = urlSafeBase64.substring(0, 20); // Adjust the length as needed
        // And this ruins the ability to go back to the uuid representation, however otherwise case sensitivity will be a mess
        return shorterUUID.toLowerCase();
    }

    /**
     * Create array of instantiated objects from database json (deserialize)
     */
    public fromJsonArray<T>(json: any[], T): T[] {
        return json ? Object.keys(json).map(key => new T(json[key], key)) : [];
    }

    /**
     * Create a serialized version of a DatabaseModel array
     */
    public toJsonArray<T>(arr: DatabaseModel[]): any[] {
        return arr ? arr.map(obj => obj.toJson()) : [];
    }

    /**
     * Create a map of instantiated objects from database json map (deserialize)
     */
    public fromJsonMap<T>(json: ModelJson, T: ModelConstructor): { [key: string]: T } {
        if(!json) return {};
        // Create array of objects    
        let objArray = Object.keys(json).map(key => new T(json[key], key));
        // Create map with object using key as index
        return objArray.reduce((objMap, model) => {
            // Assign the model instance to key in the map
            objMap[model.key] = model;
            return objMap;
        }, {});
    }

    /**
     * Create DatabaseModel map from json (json map to model array) 
     */
    public fromJsonMapToArray<T>(json: ModelJson, T: ModelConstructor): T[] {
        let sectionMap = this.fromJsonMap<T>(json, T);
        return DatabaseModel.mapToArray(sectionMap);
    }

    /**
     * Serialize DatabaseModel array (from array to json map) 
     */
    public toJsonMapFromArray<T extends DatabaseModel>(modelArray: T[]): { [key: string]: ModelJson } {
        return modelArray.reduce((modelMap, model) => {
            modelMap[model.key] = model.toJson();
            return modelMap;
        }, {});
    }

    /**
     * Create a serialized version of a DatabaseModel map
     */
    public toJsonMap<T>(map: { [key: string]: DatabaseModel }): { [key: string]: any } {
        if(!map) return {};
        // Create map with object using key as index
        return Object.keys(map).reduce((modelMap, key) => {
            // Get the model instance
            let model = map[key];
            // Add serialized version of object in new map
            modelMap[key] = model.toJson();
            return modelMap;
        }, {});
    }

    public static mapToArray<T>(modelMap: { [key: string]: T }): T[] {
        if(!modelMap) return [];
        return Object.keys(modelMap).map(key => modelMap[key]);
    }

    public static arrayToMap<T extends DatabaseModel>(modelArr: T[], keyProperty: string = 'key'): ModelMap<T> {
        if(!modelArr) return {};
        // Create map with object using key as index
        return modelArr.reduce((modelMap, model) => {
            modelMap[model[keyProperty]] = model;
            return modelMap;
        }, {});
    }

    public static genKeyFromTitle(title: string) {
        let key = title.toLowerCase().trim();

        // Digraphs / letters that do not safely reduce via NFD (Nordic, German, Romanian).
        // Without this, non-ASCII letters are deleted later → e.g. Constanța → constana.
        key = key
            .replace(/æ/g, "ae")
            .replace(/ø/g, "oe")
            .replace(/å/g, "aa")
            .replace(/ß/g, "ss")
            .replace(/œ/g, "oe")
            .replace(/[țţ]/g, "t")
            .replace(/[șş]/g, "s");

        // Decompose accented Latin (á, ü, ñ, …) and strip combining marks → base letter.
        key = key.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

        // Replace special chars not suitable for url (use hyphens)
        key = key.replace(/[\s\-\/]/g, "-").replace(/[\.\?]/g, "");
        key = key.replace(/[\(\)]/g, "").replace(/[\$:!"']/g, "");
        // Remove any leading numbers from key
        key = key.replace(/^\d+-+\s*/, "");
        key = key.replace(/-+/g, "-");

        // Remove any remaining non-ascii characters (outside 0 to 127)
        key = key.replace(/[^\x00-\x7F]/g, "");

        // Strip any URL-unsafe chars (e.g. ; ? #) to prevent broken URLs
        key = key.replace(/[^a-z0-9_-]/g, "");
        key = key.replace(/-+/g, "-").replace(/^-|-$/g, "");

        if (!key) {
            return DatabaseModel.genKey();
        }
        return key;
    }

    /**
     * Extract members of a object and add them to a new object
     */
    public static extract<T extends object, K extends keyof T>(obj: T, keys: K[]): Pick<T, K> {
        const ret = {} as Pick<T, K>;
        keys.forEach(k => ret[k] = obj[k])
        return ret;
    }
}
